import React, { useState, useEffect, useCallback } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Card, CardContent } from "../../components/ui/Card";
import { themesApi } from "../../utils/api";
import { VocabularyView } from "./VocabularyView";

// Below this share the vocabulary has a long near-duplicate tail. It is a read on the data, not a
// threshold anything acts on — nothing here merges themes.
const FRAGMENTED_TOP_N_SHARE = 50;

function StatCard({ value, label, hint, tone = "" }) {
  return (
    <Card>
      <CardContent>
        <div className={`text-2xl font-bold ${tone}`}>{value ?? "—"}</div>
        <p className="text-xs text-muted-foreground">{label}</p>
        {hint && <p className="mt-1 text-[11px] text-muted-foreground/80">{hint}</p>}
      </CardContent>
    </Card>
  );
}

/**
 * The free-text theme vocabulary the classifier produces: how concentrated it is, and every theme
 * with the events behind it.
 *
 * Read-only by design. Nothing on this page writes — it exists so the vocabulary's shape is
 * visible, which it previously was not anywhere.
 */
export default function ThemesPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  // Bumped by Refresh so the list reloads with the stats; otherwise the two can disagree.
  const [refreshKey, setRefreshKey] = useState(0);
  // One banner, but an error per source ("stats", "list"): each clears only on its own next
  // success, so one request succeeding cannot hide another that is still failing.
  const [errors, setErrors] = useState({});
  const reportError = useCallback(
    // Returning `prev` when nothing changed skips a page re-render on every successful load.
    (source, message) =>
      setErrors((prev) => ((prev[source] ?? null) === message ? prev : { ...prev, [source]: message })),
    []
  );
  const error = Object.values(errors).find(Boolean);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      setStats(await themesApi.getStats());
      reportError("stats", null);
    } catch (e) {
      reportError("stats", e.response?.data?.detail || "Failed to load theme stats");
    } finally {
      setLoading(false);
    }
  }, [reportError]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // With no references the share is 0.0, which is not a measurement; before stats load (or when
  // they fail) there is none either. Only a real share gets a verdict.
  const measured = stats != null && stats.distinct_used > 0;
  const fragmented = measured && stats.top_n_share < FRAGMENTED_TOP_N_SHARE;
  const shareHint = !measured
    ? stats && "No theme references yet"
    : fragmented
      ? "Long near-duplicate tail"
      : "Compact head";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Themes</h1>
          <p className="text-sm text-muted-foreground">
            The free-text vocabulary the classifier produces, and the events behind each phrase.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            fetchStats();
            setRefreshKey((key) => key + 1);
          }}
          disabled={loading}
        >
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-700">
          {error}
          <button className="ml-2 underline" onClick={() => setErrors({})}>
            Close
          </button>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard value={stats?.themes_total?.toLocaleString()} label="Canonical themes" />
        <StatCard
          value={stats?.distinct_used?.toLocaleString()}
          label="Phrases events use"
          hint="Distinct values across active events"
        />
        <StatCard
          value={measured ? `${stats.top_n_share}%` : null}
          label={`Share held by the top ${stats?.top_n ?? 50}`}
          hint={shareHint}
          tone={!measured ? "" : fragmented ? "text-amber-600" : "text-green-600"}
        />
      </div>

      <VocabularyView onError={reportError} refreshKey={refreshKey} />
    </div>
  );
}
